import type { FC, MouseEvent as ReactMouseEvent, ReactNode } from 'react'
import type { HeaderScheme } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowRight, ArrowUpRight, ChevronDown, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { onFrame, resumeScroll, stopScroll } from '@/lib/motion/engine'
import { detectScheme } from '@/lib/motion/scheme'
import { Logo } from '@/components/generals/logo'
import { CONTACT, LINKS } from '@/constants/site'
import { RESOURCES, TOOLS } from '@/constants/navigation'

type Panel = 'tools' | 'resources' | null

interface HeaderProps {
    initialScheme: HeaderScheme
}

const NAV = [
    { label: 'Priser', href: '/priser' },
    { label: 'Om oss', href: '/om-svenn' },
    { label: 'Kontakt', href: '/kontakt' }
]

const MOBILE_LINKS = [
    { label: 'Priser', href: '/priser' },
    { label: 'Blogg', href: '/blogg' },
    { label: 'Webinar', href: '/webinar' },
    { label: 'Håndverkerordboken', href: '/haandverkerordboken' },
    { label: 'Om oss', href: '/om-svenn' },
    { label: 'Ledige stillinger', href: '/ledige-stillinger' },
    { label: 'Kontakt', href: '/kontakt' }
]

/** Only the very first page load draws the signature; later page changes show it finished. */
let firstMount = true

export const Header: FC<HeaderProps> = ({ initialScheme }): ReactNode => {
    const router = useRouter()
    const headerRef = useRef<HTMLElement>(null)
    const closeTimer = useRef<number | undefined>(undefined)
    const [drawLogo] = useState(firstMount)

    const [panel, setPanel] = useState<Panel>(null)
    const [preview, setPreview] = useState(0)
    const [mobileOpen, setMobileOpen] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [solid, setSolid] = useState(false)
    const [scheme, setScheme] = useState<HeaderScheme>(initialScheme)

    const state = useRef({ hidden: false, solid: false, scheme: initialScheme, lastY: 0 })

    useEffect(() => {
        firstMount = false
    }, [])

    useEffect(() => {
        return onFrame(scrollY => {
            const s = state.current
            const delta = scrollY - s.lastY

            let nextHidden = s.hidden

            if (scrollY < 120) nextHidden = false
            else if (delta > 4) nextHidden = true
            else if (delta < -4) nextHidden = false

            s.lastY = scrollY

            if (nextHidden !== s.hidden) {
                s.hidden = nextHidden
                setHidden(nextHidden)
            }

            const nextSolid = scrollY > 24

            if (nextSolid !== s.solid) {
                s.solid = nextSolid
                setSolid(nextSolid)
            }

            const nextScheme = detectScheme(window.innerWidth / 2, 34, headerRef.current)

            if (nextScheme !== s.scheme) {
                s.scheme = nextScheme
                setScheme(nextScheme)
            }
        })
    }, [])

    const closeAll = useCallback((): void => {
        setPanel(null)
        setMobileOpen(false)
    }, [])

    useEffect(() => {
        router.events.on('routeChangeStart', closeAll)

        return () => router.events.off('routeChangeStart', closeAll)
    }, [router, closeAll])

    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') closeAll()
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [closeAll])

    useEffect(() => {
        if (!mobileOpen) return

        stopScroll()
        document.documentElement.style.overflow = 'hidden'

        return () => {
            resumeScroll()
            document.documentElement.style.overflow = ''
        }
    }, [mobileOpen])

    useEffect(() => {
        return () => window.clearTimeout(closeTimer.current)
    }, [])

    const openPanel = (next: Panel): void => {
        window.clearTimeout(closeTimer.current)
        setPanel(next)
    }

    const togglePanel = (next: Exclude<Panel, null>, event: ReactMouseEvent<HTMLButtonElement>): void => {
        // Hovering already opened the panel, so a mouse or touch click on its trigger keeps
        // it open instead of closing it again. Keyboard presses (detail 0) still toggle.
        if (event.detail > 0 && panel === next) {
            openPanel(next)
            return
        }

        setPanel(panel === next ? null : next)
    }

    const scheduleClose = (): void => {
        window.clearTimeout(closeTimer.current)
        closeTimer.current = window.setTimeout(() => setPanel(null), 180)
    }

    const forcedDark = panel !== null || mobileOpen
    const effectiveScheme: HeaderScheme = forcedDark ? 'dark' : scheme
    const path = router.asPath.split(/[?#]/)[0]
    const isActive = (href: string): boolean => path === href || path.startsWith(`${href}/`)
    const toolsActive = TOOLS.some(tool => isActive(tool.href))
    const resourcesActive = RESOURCES.some(item => !item.external && isActive(item.href))

    return (
        <header ref={headerRef} className='fixed inset-x-0 top-0 z-50' onMouseLeave={scheduleClose}>
            <div
                className={cn(
                    'header-bar',
                    hidden && !forcedDark && 'is-hidden',
                    solid && !forcedDark && 'is-solid'
                )}
                data-scheme={effectiveScheme}
            >
                <div className='wrap flex h-[var(--header-h)] items-center justify-between gap-6'>
                    <Link href='/' className='relative block w-[5.25rem] md:w-[5.75rem]' aria-label='Svenn, til forsiden'>
                        <Logo draw={drawLogo} decorative delay={0.25} className='w-full' />
                    </Link>

                    <nav aria-label='Hovedmeny' className='hidden items-center gap-1 lg:flex'>
                        <div onMouseEnter={() => openPanel('tools')}>
                            <button
                                type='button'
                                className={cn('header-link', toolsActive && 'is-active')}
                                aria-expanded={panel === 'tools'}
                                aria-controls='menu-tools'
                                onClick={event => togglePanel('tools', event)}
                            >
                                Verktøy
                                <ChevronDown className={cn('size-4 transition-transform duration-500', panel === 'tools' && 'rotate-180')} aria-hidden='true' />
                            </button>

                            <div id='menu-tools' className='mega-panel tone-deep grain' data-open={panel === 'tools'}>
                                <div className='grid-lines' />

                                <div className='wrap relative grid grid-cols-12 gap-x-6 pt-[calc(var(--header-h)+2.5rem)] pb-10'>
                                    <div className='col-span-8'>
                                        <p className='t-label mb-6 text-tone-muted'>Verktøy for hele jobben, fra befaring til faktura</p>

                                        <ul className='grid grid-cols-2 gap-x-10'>
                                            {TOOLS.map((tool, index) => (
                                                <li key={tool.slug} className='border-t border-tone-line'>
                                                    <Link
                                                        href={tool.href}
                                                        className='mega-link group flex items-baseline gap-4 py-4'
                                                        onMouseEnter={() => setPreview(index)}
                                                        onFocus={() => setPreview(index)}
                                                        aria-current={isActive(tool.href) ? 'page' : undefined}
                                                    >
                                                        <span className='t-label tabular w-6 shrink-0 text-tone-muted'>
                                                            {String(index + 1).padStart(2, '0')}
                                                        </span>
                                                        <span className='flex-1'>
                                                            <span className='mega-link-title block text-2xl font-semibold tracking-tight'>
                                                                {tool.name}
                                                            </span>
                                                            <span className='mt-1 block text-sm text-tone-muted'>
                                                                {tool.tagline}
                                                            </span>
                                                        </span>
                                                        <ArrowRight className='size-5 shrink-0 -translate-x-2 text-signal opacity-0 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100' aria-hidden='true' />
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className='col-span-4 flex flex-col'>
                                        <div className='relative aspect-[4/3.4] overflow-hidden bg-night-raised'>
                                            {TOOLS.map((tool, index) => (
                                                <img
                                                    key={tool.slug}
                                                    src={tool.image}
                                                    alt=''
                                                    loading='lazy'
                                                    decoding='async'
                                                    className={cn(
                                                        'absolute inset-0 size-full object-cover transition-[opacity,transform] duration-700 ease-out-quint',
                                                        preview === index ? 'scale-100 opacity-100' : 'scale-105 opacity-0'
                                                    )}
                                                />
                                            ))}
                                            <span className='t-label absolute bottom-3 left-3 bg-night px-2 py-1 text-paper'>
                                                {TOOLS[preview].name}
                                            </span>
                                        </div>

                                        <div className='mt-6 flex flex-col gap-3 text-sm'>
                                            <Link href='/priser' className='arrow-link inline-flex items-center gap-2 font-semibold'>
                                                <span className='u-link-static'>Se alle priser</span>
                                                <ArrowRight className='arrow-link-icon size-4' aria-hidden='true' />
                                            </Link>
                                            <Link href='/bli-oppringt' className='arrow-link inline-flex items-center gap-2 font-semibold'>
                                                <span className='u-link-static'>Bli oppringt av en rådgiver</span>
                                                <ArrowRight className='arrow-link-icon size-4' aria-hidden='true' />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div onMouseEnter={() => openPanel('resources')}>
                            <button
                                type='button'
                                className={cn('header-link', resourcesActive && 'is-active')}
                                aria-expanded={panel === 'resources'}
                                aria-controls='menu-resources'
                                onClick={event => togglePanel('resources', event)}
                            >
                                Ressurser
                                <ChevronDown className={cn('size-4 transition-transform duration-500', panel === 'resources' && 'rotate-180')} aria-hidden='true' />
                            </button>

                            <div id='menu-resources' className='mega-panel tone-deep grain' data-open={panel === 'resources'}>
                                <div className='grid-lines' />

                                <div className='wrap relative pt-[calc(var(--header-h)+2.5rem)] pb-12'>
                                    <p className='t-label mb-6 text-tone-muted'>Lær mer, tjen mer</p>

                                    <ul className='grid grid-cols-4 gap-x-6'>
                                        {RESOURCES.map((item, index) => {
                                            const inner = (
                                                <>
                                                    <span className='t-label tabular text-tone-muted'>
                                                        {String(index + 1).padStart(2, '0')}
                                                    </span>
                                                    <span className='mega-link-title mt-6 flex items-center gap-2 text-3xl font-semibold tracking-tight'>
                                                        {item.label}
                                                        {item.external && <ArrowUpRight className='size-5 text-signal' aria-hidden='true' />}
                                                    </span>
                                                    <span className='mt-2 block text-sm text-tone-muted'>
                                                        {item.description}
                                                    </span>
                                                </>
                                            )

                                            return (
                                                <li key={item.href} className='border-t border-tone-line'>
                                                    {item.external
                                                        ? (
                                                            <a href={item.href} className='mega-link group block pt-5' target='_blank' rel='noopener noreferrer'>
                                                                {inner}
                                                                <span className='sr-only'> (åpnes i ny fane)</span>
                                                            </a>
                                                        )
                                                        : (
                                                            <Link href={item.href} className='mega-link group block pt-5' aria-current={isActive(item.href) ? 'page' : undefined}>
                                                                {inner}
                                                            </Link>
                                                        )}
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {NAV.map(item => (
                            <Link
                                key={item.href}
                                href={item.href}
                                className={cn('header-link', isActive(item.href) && 'is-active')}
                                aria-current={isActive(item.href) ? 'page' : undefined}
                                onMouseEnter={scheduleClose}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    <div className='flex items-center gap-2 md:gap-3'>
                        <a href={LINKS.login} className='header-link from-md'>
                            Logg inn
                        </a>

                        <a href={LINKS.signup} className='btn btn-sm from-sm'>
                            Prøv gratis
                        </a>

                        <button
                            type='button'
                            className='header-link below-lg'
                            aria-expanded={mobileOpen}
                            aria-controls='menu-mobile'
                            onClick={() => setMobileOpen(open => !open)}
                        >
                            <span className='relative block h-3 w-5' aria-hidden='true'>
                                <span className={cn('absolute left-0 top-0 h-[1.5px] w-full bg-current transition-transform duration-500', mobileOpen && 'translate-y-[5px] rotate-45')} />
                                <span className={cn('absolute bottom-0 left-0 h-[1.5px] w-full bg-current transition-transform duration-500', mobileOpen && '-translate-y-[5px] -rotate-45')} />
                            </span>
                            <span>{mobileOpen ? 'Lukk' : 'Meny'}</span>
                        </button>
                    </div>
                </div>
            </div>

            <div id='menu-mobile' className='mobile-panel tone-deep grain lg:hidden' data-open={mobileOpen}>
                <div className='flex h-full flex-col overflow-y-auto pt-[calc(var(--header-h)+1rem)]' data-lenis-prevent>
                    <div className='wrap flex-1'>
                        <p className='t-label mb-4 text-tone-muted'>Verktøy</p>

                        <ul className='grid grid-cols-1 border-t border-tone-line sm:grid-cols-2 sm:gap-x-6'>
                            {TOOLS.map((tool, index) => (
                                <li key={tool.slug} className='border-b border-tone-line'>
                                    <Link href={tool.href} className='flex items-baseline gap-3 py-3.5'>
                                        <span className='t-label tabular text-tone-muted'>{String(index + 1).padStart(2, '0')}</span>
                                        <span className='text-xl font-semibold tracking-tight'>{tool.name}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <ul className='mt-10 flex flex-col gap-1'>
                            {MOBILE_LINKS.map(item => (
                                <li key={item.href}>
                                    <Link href={item.href} className='t-h2 block py-1'>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='wrap mt-10 flex flex-col gap-4 border-t border-tone-line py-6 sm:flex-row sm:items-center sm:justify-between'>
                        <a href={CONTACT.phoneHref} className='inline-flex items-center gap-2 text-tone-muted'>
                            <Phone className='size-4' aria-hidden='true' />
                            {CONTACT.phone}
                        </a>

                        <div className='flex gap-3'>
                            <a href={LINKS.login} className='btn btn-ghost flex-1 sm:flex-none'>Logg inn</a>
                            <a href={LINKS.signup} className='btn flex-1 sm:flex-none'>Prøv gratis</a>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    )
}
